"use client";

import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

const orderButtons = [
  {
    name: "Carryout",
    href: "https://goodthangzcookin.cloveronline.com/menu/all",
    logo: null,
  },
  {
    name: "DoorDash",
    href: "https://order.online/store/23989040?pickup=true&hideModal=true&redirected=true",
    logo: "https://cdn.nicktronicit.dev/doordash.png",
  },
  {
    name: "Uber Eats",
    href: "https://www.ubereats.com/store/good-thangz-cookin/vzjcf3lORAuDLTqMUUWXXw",
    logo: "https://cdn.nicktronicit.dev/ubereats.png",
  },
  {
    name: "Grubhub",
    href: "https://www.grubhub.com/restaurant/good-thangz-cookin-llc-1506-shepherd-ln-cincinnati/4052864",
    logo: "https://cdn.nicktronicit.dev/grubhub.png",
  },
];

const heroImages = [
  "https://cdn.nicktronicit.dev/gtc/gallery7.jpg",
  "https://cdn.nicktronicit.dev/gtc/gallery28.jpg",
  "https://cdn.nicktronicit.dev/gtc/gallery15.jpg",
  "https://cdn.nicktronicit.dev/gtc/gallery17.jpg",
];

import { promoBanners as promoBannersRaw } from "@/lib/promoBanners";

// ensure promo banners are ordered from oldest -> newest by expireAt

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5 },
  },
};

export default function HomePage() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const promoBanners = promoBannersRaw.slice().sort((a, b) => a.expireAt - b.expireAt);

  const [activeBanners, setActiveBanners] = useState(promoBanners);

  useEffect(() => {
    setMounted(true);
    const now = Math.floor(Date.now() / 1000);
    setActiveBanners(promoBanners.filter((b) => b.expireAt > now));
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Order Now */}
      <section className="container mx-auto px-4 py-12 md:py-20">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 items-center">
          {/* Left Side - Order Buttons */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="space-y-6"
          >
            {mounted && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="flex justify-center lg:justify-start mb-6"
              >
                <Image
                  src={
                    resolvedTheme === "dark"
                      ? "https://cdn.nicktronicit.dev/gtc/logo-d.ico"
                      : "https://cdn.nicktronicit.dev/gtc/logo.ico"
                  }
                  alt="Good Thangz Cookin Logo"
                  width={200}
                  height={200}
                  className="object-contain w-[120px] h-[120px] md:w-[200px] md:h-[200px]"
                  priority
                />
              </motion.div>
            )}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-balance text-foreground font-[family-name:var(--font-display)]">
              Order Authentic Soul Food in Cincinnati, OH
            </h1>
            <p className="text-lg text-muted-foreground text-pretty">
              Craving real soul food in Cincinnati? Good Thangz Cookin is a
              family-owned soul food restaurant serving up Southern classics
              made from scratch. Order carryout for pickup at our new Lockland
              location or get hot delivery through DoorDash, Uber
              Eats, or Grubhub — taste the love in every bite.
            </p>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              {orderButtons.map((button) => (
                <motion.div key={button.name} variants={fadeInUp}>
                  <Button
                    asChild
                    size="lg"
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold transition-all hover:scale-105"
                  >
                    <a
                      href={button.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2"
                    >
                      {button.logo && (
                        <Image
                          src={button.logo || "/placeholder.svg"}
                          alt={`${button.name} logo`}
                          width={24}
                          height={24}
                          className="object-contain"
                        />
                      )}
                      <span>{button.name}</span>
                    </a>
                  </Button>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Side - Image Grid */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-2 gap-4"
          >
            {heroImages.map((src, index) => (
              <motion.div
                key={index}
                variants={imageVariants}
                className="relative aspect-square overflow-hidden rounded-lg w-fit mx-auto"
              >
                <Image
                  src={src || "/placeholder.svg"}
                  alt={`Good Thangz Cookin food ${index + 1}`}
                  width={300}
                  height={300}
                  className="object-cover hover:scale-105 transition-transform duration-300 rounded-lg"
                  sizes="(max-width: 768px) 50vw, 25vw"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Good Thangz Cookin Section */}
      <section className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-balance text-foreground mb-4 font-[family-name:var(--font-display)]">
              The Best Soul Food Restaurant in Cincinnati
            </h2>
            <p className="text-lg text-muted-foreground text-pretty leading-relaxed">
              At Good Thangz Cookin, every plate tells a story. We pour
              generations of Southern cooking tradition into every meal —
              slow-simmered collard greens, golden fried chicken, creamy mac and
              cheese, fall-off-the-bone ribs, and homestyle sides that taste
              like Sunday dinner at grandma&apos;s house. Whether you&apos;re
              grabbing a quick lunch, feeding the family, or planning a big
              event, our Cincinnati kitchen is ready to serve.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid gap-6 md:grid-cols-3"
          >
            <motion.div variants={fadeInUp}>
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardContent className="p-6 space-y-3">
                  <h3 className="text-xl font-bold text-foreground font-[family-name:var(--font-display)]">
                    Dine-In &amp; Carryout
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Visit our new Mill Street location in Lockland Tuesday
                    through Sunday for fresh soul food made daily. Quick pickup
                    available for carryout orders.
                  </p>
                  <Link
                    href="/contact"
                    className="inline-block text-accent font-semibold hover:underline"
                  >
                    Find our location →
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardContent className="p-6 space-y-3">
                  <h3 className="text-xl font-bold text-foreground font-[family-name:var(--font-display)]">
                    Catering for Events
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    From family reunions and weddings to corporate lunches, we
                    cater authentic soul food across the Cincinnati area with
                    our full menu available.
                  </p>
                  <Link
                    href="/catering"
                    className="inline-block text-accent font-semibold hover:underline"
                  >
                    Book catering →
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardContent className="p-6 space-y-3">
                  <h3 className="text-xl font-bold text-foreground font-[family-name:var(--font-display)]">
                    See Our Food
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Browse photos of our signature dishes, daily specials, and
                    catering spreads in our gallery.
                  </p>
                  <Link
                    href="/gallery"
                    className="inline-block text-accent font-semibold hover:underline"
                  >
                    View gallery →
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-muted/30 py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={imageVariants}
              className="space-y-3"
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-lg border-2 border-accent/20">
                <Image
                  src="/images/design-mode/chefa.png"
                  alt="Chef Allen at Good Thangz Cookin"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
              </div>
              <p className="text-center text-sm text-muted-foreground italic">
                Chef Allen's Mother's Day Brunch
              </p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              className="space-y-4"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-balance text-foreground font-[family-name:var(--font-display)]">
                About Good Thangz Cookin
              </h2>
              <p className="text-lg text-muted-foreground text-pretty leading-relaxed">
                Good Thangz Cookin was founded by Chef Allen in 2022. Chef Allen
                opened his first location on Shepherd Lane with hopes of bringing
                good soul food to the Cincinnati community.
              </p>
              <p className="text-lg text-muted-foreground text-pretty leading-relaxed">
                Every dish is prepared with love, using traditional recipes and
                the finest ingredients to create an authentic soul food
                experience that brings people together.
              </p>
              <p className="text-lg text-muted-foreground text-pretty leading-relaxed">
                Now located at 102 Mill Street in Lockland, OH, our kitchen
                has quickly become a local favorite for classic Southern comfort
                food. From our signature fried chicken to our cornbread, yams,
                and peach cobbler, we serve dishes the way they were meant to be
                made — slow, seasoned, and full of flavor.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-4xl mx-auto space-y-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-foreground font-[family-name:var(--font-display)]">
              Events
            </h2>
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              {activeBanners.map((banner) => (
                  <motion.div
                    key={banner.id}
                    variants={imageVariants}
                    className="rounded-xl overflow-hidden border border-border bg-background shadow-sm"
                  >
                    <Image
                      src={banner.imageSrc}
                      loading="eager"
                      alt={`Promotion ${banner.id}`}
                      width={600}
                      height={850}
                      className="object-cover w-full h-full"
                      sizes="(max-width: 768px) 100vw, 50vw"
                    />
                  </motion.div>
                ))}
            </div>
          </motion.div>
        </div>
      </section>
      {/* Location Section */}
      <section className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-4xl mx-auto space-y-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-foreground font-[family-name:var(--font-display)]">
              Hours
            </h2>
            <Card>
              <CardContent className="p-6 md:p-8">
                <div className="space-y-3 text-left max-w-md mx-auto">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="font-semibold text-foreground">
                      Monday
                    </span>
                    <span className="text-muted-foreground">Closed</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="font-semibold text-foreground">
                      Tuesday - Saturday
                    </span>
                    <span className="text-muted-foreground">
                      11 AM - 8:30 PM
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-semibold text-foreground">
                      Sunday
                    </span>
                    <span className="text-muted-foreground">12 PM - 5 PM</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-foreground font-[family-name:var(--font-display)]">
              Visit Us
            </h2>
            <Card>
              <CardContent className="p-0">
                <div className="aspect-video w-full">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3089.8!2d-84.4574!3d39.2292!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s102%20Mill%20Street%2C%20Lockland%2C%20OH%2045215!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Good Thangz Cookin Location"
                    className="rounded-lg"
                  />
                </div>
              </CardContent>
            </Card>
            <div className="mt-6 text-center">
              <p className="text-lg font-semibold text-foreground">
                102 Mill Street, Lockland, OH 45215
              </p>
              <p className="text-muted-foreground mt-2">
                <a
                  href="tel:+15139794101"
                  className="hover:text-accent transition-colors"
                >
                  (513) 979-4101
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
